import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { ArrowRight, Bell, BellOff, Bot, CalendarClock, Check, Circle, CircleAlert, MessageCircle, Star, UserPlus } from "lucide-react";
import api from "../../services/apiService";
import { KIND_TONE, NOTE_TABS, timeAgo } from "../components/NotificationsBell";
import { InlineLoader, PageLoader } from "../components/PageLoader";

/**
 * Notifications page (design: screens/Notifications.tsx), on the same API as the bell:
 *   GET  /api/notifications?category=&unread=true&limit=100   the list
 *   GET  /api/notifications/unread-count                       tab counts
 *   PATCH /api/notifications/:id/read                          open / mark as read
 *   PATCH /api/notifications/:id/unread                        the dot on a read row (item 268)
 *   POST /api/notifications/read-all { category }              "Mark … as read"
 *
 * The Support tab is the "support" category (complaints and feedback, item 2),
 * and priority "urgent" shows as "Needs action" (item 3). The list is the
 * newest 100.
 */

// Same mappings as components/NotificationsBell.js (not exported there).
const TAB_CATEGORY = { All: "", WhatsApp: "waba", CRM: "crm", "AI Agent": "ai_agent", Support: "support" };
const CATEGORY_KIND = { waba: "WhatsApp", crm: "CRM", ai_agent: "AI Agent", support: "Support", billing: "Wallet", marketing: "Marketing" };
const LINK_ROUTES = {
  conversation: (id) => `/waba/conversations/${id}`,
  lead: (id) => `/crm/leads/${id}`,
  appointment: (id) => `/appointments/${id}`,
  complaint: (id) => `/support/complaints?open=${id}`,
  feedback: (id) => `/support/feedback?open=${id}`,
  ai_conversation: () => "/waba/conversations?tab=ai_agent",
};
const LINK_ICON = { conversation: MessageCircle, lead: UserPlus, appointment: CalendarClock, complaint: CircleAlert, feedback: Star, ai_conversation: Bot };
const CATEGORY_ICON = { waba: MessageCircle, crm: UserPlus, ai_agent: Bot, support: CircleAlert };

const toNote = (n) => {
  const route = LINK_ROUTES[n.link_type];
  return {
    id: n.id,
    kind: CATEGORY_KIND[n.category] || "CRM",
    title: n.title,
    detail: n.body || "",
    at: new Date(n.created_at).getTime(),
    href: route ? route(n.link_id) : null,
    icon: LINK_ICON[n.link_type] || CATEGORY_ICON[n.category] || Bell,
    read: !!n.is_read,
    urgent: n.priority === "urgent",
  };
};

const DAY_MS = 24 * 60 * 60 * 1000;

function groupOf(at) {
  const now = new Date();
  const start = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime();
  if (at >= start) return "Today";
  if (at >= start - DAY_MS) return "Yesterday";
  if (at >= start - 6 * DAY_MS) return "This week";
  return "Earlier";
}

export default function Notifications() {
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const fromUrl = params.get("tab");
  const tab = fromUrl && NOTE_TABS.includes(fromUrl) ? fromUrl : "All";
  const [onlyUnread, setOnlyUnread] = useState(false);
  const [notes, setNotes] = useState([]);
  const [counts, setCounts] = useState({ total: 0, byCategory: {} });
  const [countsLoaded, setCountsLoaded] = useState(false);
  // The tab / Unread only the list on screen was loaded for. A new tab shows the
  // loader rather than the previous tab's rows; a late answer for an old tab is dropped.
  const listKey = `${tab}|${onlyUnread}`;
  const [notesKey, setNotesKey] = useState(null);
  const latestKey = useRef(listKey);
  latestKey.current = listKey;
  const loading = notesKey !== listKey;

  const fetchCounts = useCallback(async () => {
    try {
      const { data } = await api.get("/notifications/unread-count");
      if (data.success) setCounts({ total: data.total || 0, byCategory: data.byCategory || {} });
    } catch (_) {
      /* keep the last counts */
    }
    setCountsLoaded(true);
  }, []);

  const fetchNotes = useCallback(async () => {
    const category = TAB_CATEGORY[tab];
    const key = `${tab}|${onlyUnread}`;
    let next;
    try {
      const query = { limit: 100 };
      if (category) query.category = category;
      if (onlyUnread) query.unread = "true";
      const { data } = await api.get("/notifications", { params: query });
      next = data.success ? (data.notifications || []).map(toNote) : [];
    } catch (_) {
      next = [];
    }
    if (latestKey.current !== key) return;
    setNotes(next);
    setNotesKey(key);
  }, [tab, onlyUnread]);

  useEffect(() => {
    fetchCounts();
  }, [fetchCounts]);
  useEffect(() => {
    fetchNotes();
  }, [fetchNotes]);

  const setTab = (t) => {
    const next = new URLSearchParams(params);
    if (t === "All") next.delete("tab");
    else next.set("tab", t);
    setParams(next, { replace: true });
  };
  const unreadIn = (t) => (t === "All" ? counts.total : TAB_CATEGORY[t] ? counts.byCategory?.[TAB_CATEGORY[t]] || 0 : 0);
  const unreadHere = unreadIn(tab);
  const list = notes;

  const groups = useMemo(() => {
    const order = ["Today", "Yesterday", "This week", "Earlier"];
    const map = new Map();
    list.forEach((n) => {
      const g = groupOf(n.at);
      map.set(g, [...(map.get(g) ?? []), n]);
    });
    return order.filter((g) => map.has(g)).map((g) => ({ label: g, items: map.get(g) }));
  }, [list]);

  const markRead = async (n) => {
    if (n.read) return;
    setNotes((prev) => (onlyUnread ? prev.filter((x) => x.id !== n.id) : prev.map((x) => (x.id === n.id ? { ...x, read: true } : x))));
    try {
      await api.patch(`/notifications/${n.id}/read`, {});
    } catch (_) {
      /* the refetch below corrects it */
    }
    fetchCounts();
  };
  const markUnread = async (n) => {
    setNotes((prev) => prev.map((x) => (x.id === n.id ? { ...x, read: false } : x)));
    try {
      await api.patch(`/notifications/${n.id}/unread`, {});
    } catch (_) {
      fetchNotes(); // put the row back as it really is
    }
    fetchCounts();
  };
  const markAll = async () => {
    const category = TAB_CATEGORY[tab];
    try {
      await api.post("/notifications/read-all", category ? { category } : {});
    } catch (_) {
      /* counts refresh below */
    }
    fetchNotes();
    fetchCounts();
  };
  const open = (n) => {
    markRead(n);
    if (n.href) navigate(n.href);
  };

  return (
    <main className="mx-auto flex w-full max-w-4xl flex-col gap-6 px-4 pb-16 pt-4 animate-fade-in sm:px-8">
      <header className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="text-h1">Notifications</h1>
          <p className="mt-1 text-small text-muted">{!countsLoaded ? <InlineLoader size={14} /> : unreadIn("All") ? `${unreadIn("All")} unread across WhatsApp, CRM, AI agents and support` : "You're all caught up"}</p>
        </div>
        <button type="button" onClick={markAll} disabled={!unreadHere} className="flex h-11 items-center gap-2 rounded-full px-5 text-small font-semibold ring-1 ring-line hover:bg-paper disabled:text-muted disabled:ring-line/60 disabled:hover:bg-transparent">
          <Check size={16} strokeWidth={2} />Mark {tab === "All" ? "all" : tab} as read
        </button>
      </header>

      <div className="flex flex-wrap items-center justify-between gap-3">
        <div role="tablist" aria-label="Filter notifications" className="flex flex-wrap gap-1.5">
          {NOTE_TABS.map((t) => {
            const c = unreadIn(t);
            return (
              <button key={t} type="button" role="tab" aria-selected={tab === t} onClick={() => setTab(t)} className={`flex h-10 items-center gap-1.5 rounded-full px-4 text-small font-semibold ${tab === t ? "bg-ink text-paper" : "bg-paper text-muted ring-1 ring-line hover:text-ink"}`}>
                {t}{c > 0 && <span className={`tabular-nums ${tab === t ? "text-paper/70" : "text-muted"}`}>{c}</span>}
              </button>
            );
          })}
        </div>
        <label className="flex cursor-pointer items-center gap-2 text-small">
          <input type="checkbox" checked={onlyUnread} onChange={(e) => setOnlyUnread(e.target.checked)} className="h-4 w-4 accent-ink" />
          Unread only
        </label>
      </div>

      <section aria-label={`${tab} notifications`} className="overflow-hidden rounded-lg bg-paper shadow-soft">
        {loading ? <PageLoader label="Loading notifications…" className="py-16" /> : groups.map((g) => (
          <div key={g.label}>
            <h2 className="border-b border-line bg-mist/60 px-5 py-2.5 text-caption font-semibold text-muted">{g.label}</h2>
            <ul>
              {g.items.map((n) => {
                const Icon = n.icon;
                const unread = !n.read;
                return (
                  <li key={n.id} className="group flex items-start gap-2 border-b border-line last:border-b-0 hover:bg-mist/50">
                    <button type="button" onClick={() => open(n)} className="flex min-w-0 flex-1 items-start gap-4 px-5 py-4 text-left">
                      <Icon size={20} strokeWidth={1.75} className={`mt-0.5 shrink-0 ${n.urgent ? "text-danger" : "text-muted"}`} />
                      <span className="min-w-0 flex-1">
                        <span className={`block text-body ${unread ? "font-semibold" : ""}`}>{n.title}</span>
                        <span className="mt-0.5 block text-small text-muted">{n.detail}</span>
                        <span className="mt-2 flex flex-wrap items-center gap-2">
                          <span className={`rounded-xs px-1.5 py-0.5 text-caption font-semibold ${KIND_TONE[n.kind]}`}>{n.kind}</span>
                          <span className="text-caption text-muted">{timeAgo(n.at)}</span>
                          {n.urgent && <span className="text-caption font-semibold text-danger">Needs action</span>}
                        </span>
                      </span>
                      <span className="mt-1 hidden shrink-0 items-center gap-1 text-small font-semibold text-sky-deep sm:flex">Open<ArrowRight size={16} strokeWidth={1.75} /></span>
                    </button>
                    <button type="button" onClick={() => (unread ? markRead(n) : markUnread(n))} aria-label={unread ? `Mark "${n.title}" as read` : `Mark "${n.title}" as unread`} title={unread ? "Mark as read" : "Mark as unread"} className="mr-3 mt-3.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-full text-muted hover:bg-paper hover:text-ink">
                      {unread ? <span className="h-2.5 w-2.5 rounded-full bg-sky-deep" /> : <Circle size={12} strokeWidth={1.75} />}
                    </button>
                  </li>
                );
              })}
            </ul>
          </div>
        ))}
        {!loading && !list.length && (
          <div className="flex flex-col items-center gap-3 px-6 py-16 text-center">
            <BellOff size={24} strokeWidth={1.75} className="text-muted" />
            <p className="text-h3">{onlyUnread ? "No unread notifications" : tab === "All" ? "No notifications yet" : `No ${tab} notifications`}</p>
            <p className="text-small text-muted">{onlyUnread ? "Turn off Unread only to see everything." : "New messages, leads and alerts will show up here."}</p>
            {onlyUnread && <button type="button" onClick={() => setOnlyUnread(false)} className="mt-1 h-10 rounded-full px-4 text-small font-semibold ring-1 ring-line hover:bg-mist">Show all</button>}
          </div>
        )}
      </section>
    </main>
  );
}
